import { createBrowserHistory } from "history";
import React, { useEffect, useRef, useState } from "react";
import useSearchParams from "@/components/hooks/useSearchParams";

interface Props {
  fullViewOnMobile?: boolean;
  setFullViewOnMobile?: (state: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  placeholder?: string;
  hasToggle?: boolean;
  toggleAction?: () => void;
  resetPage: VoidFunction;
  saveToParams?: boolean;
  inputVisible?: boolean;
}

const AppSearchBar: React.FC<Props> = ({
  fullViewOnMobile = false,
  setFullViewOnMobile = () => {},
  searchQuery,
  setSearchQuery,
  placeholder = "Search items",
  hasToggle = false,
  toggleAction,
  resetPage,
  saveToParams = true,
  inputVisible = true,
}) => {
  const { search } = useSearchParams(["search"]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (saveToParams) {
      const history = createBrowserHistory();
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("search", searchQuery);

      history.push({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    }
    resetPage();
    setFullViewOnMobile(false);
  };

  useEffect(() => {
    if (inputRef.current && inputVisible) {
      inputRef.current.focus();
    }
  }, [inputVisible]);

  useEffect(() => {
    setSearchQuery(search ?? "");
  }, [search]);

  return (
    <form
      onClick={() => (hasToggle ? toggleAction() : undefined)}
      className="flex relative flex-1 justify-end sm:flex-none w-full"
      onSubmit={handleSearch}
    >
      <input
        className={`h-12 sm:h-14 border border-grey-border border-opacity-50 rounded-lg text-xs md:text-sm placeholder-shown:text-sm no-outline text-dark flex items-center ring-0 focus:border focus:border-grey-border transition-all ease-in-out placeholder-placeholder leading-3 overflow-hidden ${
          fullViewOnMobile ? "w-full px-3 border opacity-1" : "w-full px-3.5 border sm:px-3.5"
        }`}
        placeholder={placeholder}
        value={searchQuery}
        readOnly={hasToggle ? true : false}
        onChange={(e) => setSearchQuery(e.target.value)}
        autoFocus
        ref={inputRef}
      />
      <button
        className={`items-center justify-center h-9 w-9 sm:h-10 sm:w-10 rounded-[8px] absolute top-1/2 transform -translate-y-1/2 right-1.5 text-placeholder transition-all ease-in-out duration-100 hover:text-primary-500 bg-grey-fields-200 ${
          fullViewOnMobile ? "flex" : "flex sm:flex"
        }`}
        type="submit"
        disabled={hasToggle ? true : false}
      >
        {/* prettier-ignore */}
        <svg width="45%" viewBox="0 0 18 18" fill="none">
          <mask id="mask0_5573_13849"maskUnits="userSpaceOnUse" x="0" y="0" width="17" height="17">
            <path fillRule="evenodd" clipRule="evenodd" d="M0.666687 0.666626H16.8974V16.8975H0.666687V0.666626Z" fill="white"/>
          </mask>
          <g mask="url(#mask0_5573_13849)">
            <path fillRule="evenodd" clipRule="evenodd" d="M8.78234 1.91663C4.9965 1.91663 1.9165 4.99579 1.9165 8.78163C1.9165 12.5675 4.9965 15.6475 8.78234 15.6475C12.5673 15.6475 15.6473 12.5675 15.6473 8.78163C15.6473 4.99579 12.5673 1.91663 8.78234 1.91663M8.78234 16.8975C4.30734 16.8975 0.666504 13.2566 0.666504 8.78163C0.666504 4.30663 4.30734 0.666626 8.78234 0.666626C13.2573 0.666626 16.8973 4.30663 16.8973 8.78163C16.8973 13.2566 13.2573 16.8975 8.78234 16.8975" fill="currentColor"/>
          </g>
          <mask id="mask1_5573_13849" maskUnits="userSpaceOnUse" x="13" y="13" width="5" height="5">
            <path fillRule="evenodd" clipRule="evenodd" d="M13.3666 13.7557H17.5533V17.9347H13.3666V13.7557Z" fill="white"/>
          </mask>
          <g mask="url(#mask1_5573_13849)">
            <path fillRule="evenodd" clipRule="evenodd" d="M16.9285 17.9347C16.7693 17.9347 16.6093 17.8739 16.4868 17.7522L13.5502 14.8239C13.306 14.5797 13.3052 14.1839 13.5493 13.9397C13.7927 13.6939 14.1885 13.6955 14.4335 13.938L17.3702 16.8672C17.6143 17.1114 17.6152 17.5064 17.371 17.7505C17.2493 17.8739 17.0885 17.9347 16.9285 17.9347" fill="currentColor"/>
          </g>
        </svg>
      </button>
      <button
        className={`items-center justify-center h-10 w-10 rounded-[8px] bg-grey-fields-200  text-dark transition-all ease-in-out duration-100 hover:text-primary-500 ${
          fullViewOnMobile ? "hidden flex" : "hidden flex sm:hidden"
        }`}
        onClick={() => setFullViewOnMobile(true)}
        type="button"
      >
        {/* prettier-ignore */}
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" >
          <mask id="mask0_5573_13849"maskUnits="userSpaceOnUse" x="0" y="0" width="17" height="17">
            <path fillRule="evenodd" clipRule="evenodd" d="M0.666687 0.666626H16.8974V16.8975H0.666687V0.666626Z" fill="white"/>
          </mask>
          <g mask="url(#mask0_5573_13849)">
            <path fillRule="evenodd" clipRule="evenodd" d="M8.78234 1.91663C4.9965 1.91663 1.9165 4.99579 1.9165 8.78163C1.9165 12.5675 4.9965 15.6475 8.78234 15.6475C12.5673 15.6475 15.6473 12.5675 15.6473 8.78163C15.6473 4.99579 12.5673 1.91663 8.78234 1.91663M8.78234 16.8975C4.30734 16.8975 0.666504 13.2566 0.666504 8.78163C0.666504 4.30663 4.30734 0.666626 8.78234 0.666626C13.2573 0.666626 16.8973 4.30663 16.8973 8.78163C16.8973 13.2566 13.2573 16.8975 8.78234 16.8975" fill="currentColor"/>
          </g>
          <mask id="mask1_5573_13849" maskUnits="userSpaceOnUse" x="13" y="13" width="5" height="5">
            <path fillRule="evenodd" clipRule="evenodd" d="M13.3666 13.7557H17.5533V17.9347H13.3666V13.7557Z" fill="white"/>
          </mask>
          <g mask="url(#mask1_5573_13849)">
            <path fillRule="evenodd" clipRule="evenodd" d="M16.9285 17.9347C16.7693 17.9347 16.6093 17.8739 16.4868 17.7522L13.5502 14.8239C13.306 14.5797 13.3052 14.1839 13.5493 13.9397C13.7927 13.6939 14.1885 13.6955 14.4335 13.938L17.3702 16.8672C17.6143 17.1114 17.6152 17.5064 17.371 17.7505C17.2493 17.8739 17.0885 17.9347 16.9285 17.9347" fill="currentColor"/>
          </g>
        </svg>
      </button>
    </form>
  );
};

export default AppSearchBar;
